/*
 * 86Borders LLC. ("COMPANY") CONFIDENTIAL
 *  Copyright (c) 2014-2017 86Borders LLC, All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains the property of COMPANY.
 *  The intellectual and technical concepts contained herein are proprietary to COMPANY and
 *  may be covered by U.S. and Foreign Patents, patents in process, and are protected
 *  by trade secret or copyright law.
 *
 *  Dissemination of this information or reproduction of this material in any form is strictly
 *  forbidden unless prior written permission is obtained from COMPANY. Access to the source
 *  code contained herein is hereby forbidden to anyone except current COMPANY employees,
 *  managers or contractors who have executed Confidentiality and Non-disclosure agreements
 *  explicitly covering such access.
 *
 *  The copyright notice above does not evidence any actual or intended publication or disclosure
 *  of this source code, which includes information that is confidential and/or proprietary, and
 *  is a trade secret, of COMPANY.
 *
 *  ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE,
 *  OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS
 *  WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF
 *  APPLICABLE LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF THIS
 *  SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 *  TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE,
 *  OR SELL ANYTHING THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {inject, newInstance, autoinject, LogManager} from 'aurelia-framework';
import {HttpClient} from 'aurelia-http-client';
import {handleResponse, responseContent} from '../tools/http/http';
import {Paginator} from '../tools/http/paginator';
import {noop, merge, keys, delay, difference as diff} from 'lodash';

const logger = LogManager.getLogger('UserService');

export interface User {
  id: number
  created: number
  updated: number
  nickname: string
  guid: string
  lang?: string
  email: string
  firstname?: string
  lastname?: string
  fullname?: string
  photo?: string
  photoEncoded?: string
  confirmCode?: string
  activated?: boolean
  activateDate?: number
  phone?: string
  isCallEnabled?: boolean
}

@autoinject()
export class UserService {

  constructor(@newInstance() private __httpClient: HttpClient) {
    this.__httpClient = __httpClient.configure(x => {
      x.withBaseUrl('fe/users/');
      x.withHeader('pragma', 'no-cache');
      x.withHeader('cache-control', 'no-cache');
    });
  }

  searchUsersByMask(mask: string, params = {pageSize: 10, pageNum: 0}) {
    return handleResponse(this.__httpClient.get(`search-by-mask/${mask}?pageSize=${params.pageSize}&pageNum=${params.pageNum}`));
  }

  loggedInUserDetails(): Promise<User> {
    return responseContent(this.__httpClient.get('logged-in-details'));
  }

  smartSearch({mask, groupId, pageNum, pageSize}: {mask: string, groupId: string, pageNum: number, pageSize: number}): Paginator<User> {
    return new Paginator(
      {mask, groupId, pageNum, pageSize},
      data => this.__httpClient.post('smart-search', data)
    );
  }
}

@autoinject()
export class UserDetails {

  private __data: Partial<User> = {};
  private __dataPromise: Promise<User> = null;
  private __userService: UserService;

  constructor(userService: UserService) {
    this.__userService = userService;
    void this.__requestData();
  }

  get data() {
    return this.__data;
  }

  get() {
    return (this.__dataPromise || Promise.resolve()).then(() => this.__data);
  }

  resolve(consumer: (user: Partial<User>) => void = noop) {
    this.get().then(consumer);
  }

  clone() {
    return this.get().then(() => merge({}, this.__data));
  }

  update() {
    return this.__requestData();
  }

  private __requestData() {
    return this.__dataPromise = this.__userService.loggedInUserDetails()
      .then(data => {
        this.__dataPromise = null;
        diff(keys(this.__data), keys(data)).forEach(k => this.__data[k] = null);
        return merge(this.__data, data);
      }, e => {
        if(e.statusCode==401) {
            (window.location as any)='/sign-in';
        }
        logger.error(e);
        return new Promise(resolve => {
          delay(() => resolve(this.__requestData()), 5000);
        });
      });
  }

}
